<script setup>
import { inject, ref, onMounted, onBeforeUnmount } from "vue";
import { major, minor, build } from "../../../version.json";
import { useSettingsModal } from "../../composables/useSettingsModal.js";

defineProps({
  name:      { type: String, default: "" },
  email:     { type: String, default: "" },
  initials:  { type: String, default: "?" },
  isB2BUser: { type: Boolean, default: false },
});

const emit = defineEmits(["close"]);
const $auth0 = inject("$auth0");
const isLoggingOut = ref(false);
const showAboutSubmenu = ref(false);
const { open: openSettings } = useSettingsModal();

const version = `${major}.${minor}.${build}`;

function onOpenSettings() {
  openSettings();
  emit("close");
}

async function logout() {
  isLoggingOut.value = true;
  await $auth0.signOut();
}

const menuEl = ref(null);
function onDocClick(e) {
  if (menuEl.value && !menuEl.value.contains(e.target)) emit("close");
}
onMounted(() => setTimeout(() => document.addEventListener("click", onDocClick), 0));
onBeforeUnmount(() => document.removeEventListener("click", onDocClick));
</script>

<template>
  <!-- fixed: never clipped by sidebar overflow; bottom-[72px] = above avatar; left-[88px] = sidebar edge -->
  <div data-testid="profile-menu" ref="menuEl" class="fixed bottom-[65px] left-[10px] w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl p-2.5 flex flex-col gap-0.5 z-[200]">

    <!-- User row -->
    <button class="flex items-center gap-2.5 w-full p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-left border-0 bg-transparent cursor-pointer">
      <div data-testid="profile-menu-avatar" class="flex items-center justify-center w-10 h-10 min-w-[40px] rounded-full bg-[#7ba7d4] text-white text-[13px] font-semibold shrink-0">
        {{ initials }}
      </div>
      <div class="flex flex-col flex-1 overflow-hidden">
        <span data-testid="profile-menu-user-name" class="text-[13px] font-semibold text-slate-800 dark:text-slate-100 truncate">{{ name }}</span>
        <span data-testid="profile-menu-user-email" class="text-xs text-slate-500 dark:text-slate-400 truncate">{{ email }}</span>
      </div>
      <svg class="text-slate-400 shrink-0" width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <!-- Enterprise banner — B2C users only; B2B users are already enterprise customers -->
    <div data-testid="profile-menu-enterprise-banner" v-if="!isB2BUser" class="bg-slate-100 dark:bg-slate-700 rounded-xl px-3 py-2 my-1">
      <p class="text-xs text-slate-500 dark:text-slate-400 m-0 mb-0.5">Are you an enterprise user?</p>
      <a data-testid="profile-menu-talk-to-us" class="text-xs font-medium text-blue-500 dark:text-blue-400 no-underline hover:underline" href="mailto:sales@folia.com">Talk to us</a>
    </div>

    <!-- Settings -->
    <button
      data-testid="profile-menu-settings"
      class="flex items-center gap-2.5 w-full px-2.5 py-2.5 rounded-lg text-[13px] text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors border-0 bg-transparent cursor-pointer"
      @click="onOpenSettings"
    >
      <svg class="shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.8" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" stroke-width="1.8" />
      </svg>
      <span class="flex-1 text-left">Settings</span>
    </button>

    <div class="h-px bg-slate-200 dark:bg-slate-700 my-1" />

    <!-- Help -->
    <a
      data-testid="profile-menu-help"
      href="https://help.folia.com/knowledge"
      target="_blank"
      rel="noopener noreferrer"
      class="flex items-center gap-2.5 w-full px-2.5 py-2.5 rounded-lg text-[13px] text-slate-800 dark:text-slate-200 bg-transparent dark:bg-transparent hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors no-underline cursor-pointer"
    >
      <svg class="shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        <circle cx="12" cy="17" r="0.5" fill="currentColor" stroke="currentColor" stroke-width="1" />
      </svg>
      <span class="flex-1 text-left">Help</span>
      <!-- <svg class="text-slate-400 shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg> -->
    </a>

    <!-- About with flyout -->
    <div class="relative" @mouseenter="showAboutSubmenu = true" @mouseleave="showAboutSubmenu = false">
      <button data-testid="profile-menu-about" :class="['flex items-center gap-2.5 w-full px-2.5 py-2.5 rounded-lg text-[13px] text-slate-800 dark:text-slate-200 transition-colors border-0 cursor-pointer',
        showAboutSubmenu ? 'bg-slate-100 dark:bg-slate-700' : 'bg-transparent hover:bg-slate-100 dark:hover:bg-slate-700']">
        <svg class="shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
          <line x1="12" y1="8" x2="12" y2="8" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
          <path d="M12 11v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
        <span class="flex-1 text-left">About</span>
        <svg class="text-slate-400 shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>

      <!-- Flyout submenu -->
      <div data-testid="profile-menu-about-submenu" v-if="showAboutSubmenu"
        class="absolute top-0 left-[calc(100%+6px)] w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg p-2 flex flex-col gap-0.5 z-[201]">
        <a
          data-testid="profile-menu-privacy-policy"
          href="https://www.folia.com/legal/privacy"
          target="_blank"
          rel="noopener noreferrer"
          class="block w-full px-2.5 py-2 text-left text-[13px] text-slate-800 dark:text-slate-200 bg-transparent hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg no-underline cursor-pointer transition-colors"
        >
          Privacy policy
        </a>
        <a
          data-testid="profile-menu-terms-of-service"
          href="https://www.folia.com/legal"
          target="_blank"
          rel="noopener noreferrer"
          class="block w-full px-2.5 py-2 text-left text-[13px] text-slate-800 dark:text-slate-200 bg-transparent hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg no-underline cursor-pointer transition-colors"
        >
          Terms of service
        </a>
        <div class="h-px bg-slate-200 dark:bg-slate-700 my-1" />
        <span data-testid="profile-menu-app-version" class="px-2.5 py-1.5 text-xs text-slate-400 dark:text-slate-500">Version: {{ version }}</span>
      </div>
    </div>

    <div class="h-px bg-slate-200 dark:bg-slate-700 my-1" />

    <!-- Log out -->
    <button
      data-testid="profile-menu-logout"
      class="flex items-center gap-2.5 w-full px-2.5 py-2.5 rounded-lg text-[13px] text-red-500 bg-transparent hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors border-0 cursor-pointer disabled:opacity-60 disabled:cursor-default"
      :disabled="isLoggingOut"
      @click="logout"
    >
      <svg class="shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        <polyline points="16 17 21 12 16 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      <span>{{ isLoggingOut ? "Logging out…" : "Log out" }}</span>
    </button>
  </div>
</template>
